import { createPortal } from "react-dom";
import { useEffect, type MouseEvent } from "react";
import type { ThemeFieldPreview } from "containers/theme-setter/types";

type FieldPreviewModalProps = {
  label: string;
  preview: ThemeFieldPreview;
  onClose: () => void;
};

const stopPropagation = (event: MouseEvent<HTMLDivElement>) => {
  event.stopPropagation();
};

export const FieldPreviewModal = ({
  label,
  preview,
  onClose,
}: FieldPreviewModalProps) => {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  if (typeof document === "undefined") {
    return null;
  }

  const renderSnapshots = () => {
    if (!preview.snapshots?.length) return null;
    return (
      <div className="grid gap-4">
        {preview.snapshots.map((snapshot, index) => (
          <figure
            key={`${snapshot.src}-${index}`}
            className="overflow-hidden rounded-lg border border-border bg-gray-800"
          >
            <img
              src={snapshot.src}
              alt={snapshot.alt ?? `${label} preview ${index + 1}`}
              className="w-full h-auto object-cover"
              loading="lazy"
            />
            {snapshot.caption && (
              <figcaption className="border-t border-border px-4 py-3 text-xs uppercase tracking-wide text-gray-300">
                {snapshot.caption}
              </figcaption>
            )}
          </figure>
        ))}
      </div>
    );
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 px-4 py-6"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-border bg-gray-900 shadow-2xl"
        onClick={stopPropagation}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-primary">
              {preview.title ?? label}
            </p>
            <p className="text-sm text-gray-300">
              Static preview for {label}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-border p-1.5 text-gray-300 hover:bg-gray-800"
            aria-label="Close preview"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6 6l12 12M6 18L18 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
        <div className="space-y-5 px-5 py-6">
          {preview.description && (
            <p className="text-sm leading-relaxed text-gray-200">
              {preview.description}
            </p>
          )}
          {renderSnapshots()}
          {!preview.snapshots?.length && (
            <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-gray-400">
              Add one or more snapshots to this preview by updating
              <code className="mx-1 rounded bg-gray-800 px-1.5 py-0.5 text-xs">
                field-previews.ts
              </code>
              .
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
